/**
 * "View nutrition label" dialog.
 *
 * Fetches the product page for the currently selected variant and lifts the
 * visible nutrition panel's facts card and ingredients into a modal. The
 * facts are prefetched once the page is idle, again whenever the flavor
 * picker changes, and on hover as a last resort, so opening the dialog shows
 * them instantly instead of an empty panel that fills in. Responses are
 * cached per variant. The selected variant is read live from the section's
 * flavor radios so the dialog follows the picker without any extra wiring.
 *
 * When the facts can't be loaded (the product page's nutrition section is
 * missing or the request fails) the dialog stays open with a short notice
 * and a link to the product page, rather than closing on the shopper.
 */
class NutritionPopup extends HTMLElement {
  #abortController = new AbortController();

  /** @type {Map<string, Promise<HTMLElement>>} variant id → fetch in flight or settled */
  #cache = new Map();

  /** @type {Map<string, HTMLElement>} variant id → facts ready to show synchronously */
  #ready = new Map();

  connectedCallback() {
    // A fresh controller per connection (a morph can move the node)
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const open = this.#ref('open');
    open?.addEventListener('click', () => this.#open(), { signal });
    open?.addEventListener('pointerenter', () => this.#prefetch(), { signal });
    open?.addEventListener('focus', () => this.#prefetch(), { signal });
    this.#ref('close')?.addEventListener('click', () => this.#dialog?.close(), { signal });
    this.#dialog?.addEventListener(
      'click',
      (event) => {
        // Backdrop click: the dialog itself is the target, its panel is not
        if (event.target === this.#dialog) this.#dialog?.close();
      },
      { signal }
    );

    // Follow the flavor picker: the facts for the new variant are fetched as
    // soon as it is chosen, well before anyone taps the button
    this.#section?.addEventListener(
      'change',
      (event) => {
        if (event.target instanceof HTMLInputElement && event.target.closest('.variant-picker')) this.#prefetch();
      },
      { signal }
    );

    // Warm the cache for the initial variant once the page has settled
    const idle = window.requestIdleCallback ?? ((callback) => setTimeout(callback, 800));
    idle(() => {
      if (!signal.aborted) this.#prefetch();
    });
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  get #dialog() {
    return /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-ref="dialog"]'));
  }

  /** @param {string} name */
  #ref(name) {
    return /** @type {HTMLElement | null} */ (this.querySelector(`[data-ref="${name}"]`));
  }

  get #section() {
    return this.closest('.product-information') ?? this.closest('.shopify-section');
  }

  get #variantId() {
    const checked = this.#section?.querySelector('.variant-picker input[type="radio"]:checked');
    if (checked instanceof HTMLInputElement && checked.dataset.variantId) return checked.dataset.variantId;
    return this.dataset.variantId ?? '';
  }

  /**
   * Facts for the current variant, fetched once and shared by every caller.
   * A failed fetch is dropped from the cache so the next attempt retries.
   */
  #prefetch() {
    const variantId = this.#variantId;
    const pending = this.#cache.get(variantId);
    if (pending) return pending;

    const request = this.#load(variantId).then(
      (facts) => {
        this.#ready.set(variantId, facts);
        return facts;
      },
      (error) => {
        this.#cache.delete(variantId);
        throw error;
      }
    );
    // Background callers (idle, hover, picker change) don't await this
    request.catch(() => {});
    this.#cache.set(variantId, request);
    return request;
  }

  /**
   * @param {string} variantId
   * @returns {Promise<HTMLElement>}
   */
  async #load(variantId) {
    // The product page itself (the flavor-nutrition section's full id is
    // template-prefixed and differs per theme copy, so section_id is out)
    const url = new URL(this.dataset.productUrl ?? '', location.origin);
    if (variantId) url.searchParams.set('variant', variantId);
    const response = await fetch(url.toString());
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');

    const panel =
      html.querySelector('.flavor-nutrition__panel:not([hidden])') ?? html.querySelector('.flavor-nutrition__panel');
    const facts = panel?.querySelector('.nutrition-facts');
    const ingredients = panel?.querySelector('.flavor-nutrition__block');
    const lines = panel ? [...panel.querySelectorAll('.flavor-nutrition__line')] : [];
    if (!facts) throw new Error('No nutrition facts in section');

    const wrapper = document.createElement('div');
    wrapper.className = 'nutrition-popup__facts';
    wrapper.append(facts.cloneNode(true));
    if (ingredients || lines.length) {
      const aside = document.createElement('div');
      aside.className = 'nutrition-popup__ingredients';
      if (ingredients) aside.append(ingredients.cloneNode(true));
      for (const line of lines) aside.append(line.cloneNode(true));
      wrapper.append(aside);
    }

    // Nodes come from this store's own rendered section, cloned rather than
    // serialised so nothing is re-parsed as markup
    return wrapper;
  }

  async #open() {
    const dialog = this.#dialog;
    const content = this.#ref('content');
    if (!dialog || !content) return;

    const variantId = this.#variantId;
    const request = this.#prefetch();

    // Prefetched: fill the panel before the dialog paints so it opens
    // complete. Otherwise (cold cache) it opens at once and fills in.
    const ready = this.#ready.get(variantId);
    if (ready) {
      content.replaceChildren(ready.cloneNode(true));
      dialog.showModal();
      return;
    }

    dialog.showModal();
    content.setAttribute('aria-busy', 'true');
    try {
      const facts = await request;
      // The picker may have moved on while this was loading
      if (this.#variantId !== variantId) return;
      content.replaceChildren(facts.cloneNode(true));
    } catch (error) {
      console.error('[nutrition-popup]', error);
      const fallback = this.querySelector('template[data-ref="fallback"]');
      if (fallback instanceof HTMLTemplateElement) {
        content.replaceChildren(fallback.content.cloneNode(true));
      } else {
        dialog.close();
      }
    } finally {
      content.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('nutrition-popup')) {
  customElements.define('nutrition-popup', NutritionPopup);
}
